/**
 * AdvancedTable — stats-rich, filterable, sortable data grid.
 *
 * Replaces FilterableTable in DataChat (artifacts, modals, final
 * answer tab) and DashboardPanel.
 *
 * Features
 *   - Per-column inline statistics in the header (top values + %, or
 *     min/max/avg for numeric, range for temporal, true/false for boolean).
 *     Uses dataSummary.by_column when available, falls back to client-side
 *     compute over the rows when not.
 *   - Per-column filter dropdown:
 *       * categorical / boolean → searchable multi-select with counts
 *       * numeric                → min/max range
 *       * temporal               → min/max date range
 *   - 3-state sort (asc → desc → none) by clicking the column name
 *   - Sticky header, row hover, null cell styling
 *   - Footer with row count and active-filter summary
 *
 * Props
 *   data         { columns: string[], rows: any[][], row_count?, truncated? }
 *   dataSummary  optional { by_column: { [col]: stats } } — server-computed
 *                stats from result_augment.py. When omitted the component
 *                computes its own stats client-side.
 *   maxRows      cap on rendered rows (default 200). Filtering / sorting
 *                runs over all rows; only display is capped.
 *   fill         true → fill parent height (for dashboard cards)
 */

import React, { useMemo, useState } from 'react';
import { Dropdown, Input, Button, Tooltip, DatePicker, InputNumber, Space, Tag } from 'antd';
import {
  CaretUpOutlined, CaretDownOutlined, FilterOutlined, FilterFilled,
  CalendarOutlined, NumberOutlined, FontSizeOutlined, CheckSquareOutlined,
  ReloadOutlined, BarsOutlined, ProfileOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import './AdvancedTable.css';

/* ────────────────────── Type + value helpers ────────────────────── */

const TYPE_NUMERIC = 'numeric';
const TYPE_CATEGORICAL = 'categorical';
const TYPE_TEMPORAL = 'temporal';
const TYPE_BOOLEAN = 'boolean';

function typeIconChar(type) {
  switch (type) {
    case TYPE_NUMERIC: return '#';
    case TYPE_TEMPORAL: return null; // we use the calendar Ant icon
    case TYPE_BOOLEAN: return null;  // checksquare icon
    case TYPE_CATEGORICAL:
    default: return 'A';
  }
}

function TypeIcon({ type }) {
  if (type === TYPE_TEMPORAL) return <CalendarOutlined style={{ fontSize: 11 }} />;
  if (type === TYPE_BOOLEAN) return <CheckSquareOutlined style={{ fontSize: 11 }} />;
  if (type === TYPE_NUMERIC) return <span>#</span>;
  return <span>A</span>;
}

function looksLikeDateString(v) {
  if (typeof v !== 'string') return false;
  return /^\d{4}-\d{2}-\d{2}/.test(v) || /^\d{2}\/\d{2}\/\d{4}/.test(v);
}

function detectColumnType(stats, sampleValues) {
  if (stats?.type) return stats.type;
  const nonNull = sampleValues.filter(v => v != null);
  if (!nonNull.length) return TYPE_CATEGORICAL;
  const probe = nonNull.slice(0, 30);
  if (probe.every(v => typeof v === 'boolean')) return TYPE_BOOLEAN;
  if (probe.every(v => typeof v === 'number')) return TYPE_NUMERIC;
  if (probe.every(v => looksLikeDateString(v))) return TYPE_TEMPORAL;
  return TYPE_CATEGORICAL;
}

/* When the backend didn't ship a data_summary, compute a small one
   client-side so the UI is consistent across legacy paths. */
function clientSideStats(values, type, topN = 5) {
  const non = values.filter(v => v != null);
  const nullCount = values.length - non.length;
  if (type === TYPE_NUMERIC) {
    if (!non.length) return { type, null_count: nullCount, distinct_count: 0 };
    const nums = non.map(Number).sort((a, b) => a - b);
    const sum = nums.reduce((s, x) => s + x, 0);
    const mid = Math.floor(nums.length * 0.5);
    const p90i = Math.floor(nums.length * 0.9);
    return {
      type,
      null_count: nullCount,
      distinct_count: new Set(nums).size,
      min: nums[0],
      max: nums[nums.length - 1],
      avg: sum / nums.length,
      p50: nums[mid],
      p90: nums[p90i],
      sum,
    };
  }
  if (type === TYPE_TEMPORAL) {
    if (!non.length) return { type, null_count: nullCount, distinct_count: 0 };
    const sorted = [...non].sort();
    const lo = sorted[0];
    const hi = sorted[sorted.length - 1];
    let range_days = null;
    try {
      const a = dayjs(lo); const b = dayjs(hi);
      if (a.isValid() && b.isValid()) range_days = b.diff(a, 'day');
    } catch { /* noop */ }
    return { type, null_count: nullCount, distinct_count: new Set(non).size, min: String(lo), max: String(hi), range_days };
  }
  if (type === TYPE_BOOLEAN) {
    let t = 0, f = 0;
    non.forEach(v => { if (v) t++; else f++; });
    return { type, null_count: nullCount, distinct_count: new Set(non).size, true_count: t, false_count: f };
  }
  // categorical
  const counts = new Map();
  non.forEach(v => counts.set(v, (counts.get(v) || 0) + 1));
  const total = non.length;
  const top = Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, topN)
    .map(([value, count]) => ({ value, count, pct: total > 0 ? count / total : 0 }));
  return { type, null_count: nullCount, distinct_count: counts.size, top };
}

function formatNumber(v) {
  if (v == null) return '—';
  if (typeof v !== 'number') return String(v);
  if (Number.isInteger(v)) return v.toLocaleString();
  return Number(v.toFixed(4)).toLocaleString();
}

function formatPct(p) {
  if (p == null) return '';
  return `${(p * 100).toFixed(1)}%`;
}

/* ────────────────────── Header stats block ──────────────────────── */

// Categorical inline display caps. Server may only ship 5 values via
// dataSummary, so the categorical branch prefers ``distinctValues``
// (full set computed client-side from the rendered rows) when expanded.
const COLLAPSED_TOP_N = 3;
const EXPANDED_TOP_N = 15;

function HeaderStats({ stats, type, totalRows, distinctValues, expanded, onToggleExpanded }) {
  if (!stats) return null;

  if (type === TYPE_CATEGORICAL) {
    // Source of truth for the value list: prefer the larger client-side
    // set so expansion always reaches the configured cap, even when the
    // server-side categorical_top_n is only 5.
    const source =
      distinctValues && distinctValues.length >= (stats.top?.length || 0)
        ? distinctValues
        : (stats.top || []);
    if (!source.length) return null;

    const totalNonNull = source.reduce((s, t) => s + (t.count || 0), 0);
    const cap = expanded ? EXPANDED_TOP_N : COLLAPSED_TOP_N;
    const visible = source.slice(0, cap);
    const distinctCount = stats.distinct_count ?? source.length;
    const remaining = Math.max(0, distinctCount - visible.length);
    // Bar widths reference the largest visible bucket so the visual is monotone
    // descending (Snowflake-style mini-bar look).
    const maxCount = visible[0]?.count || 1;

    return (
      <div className="advtable-stats">
        {visible.map((t, i) => {
          const pct = totalNonNull > 0 ? (t.count / totalNonNull) : 0;
          const barW = (t.count / maxCount) * 100;
          return (
            <div key={i} className="advtable-stats-row" style={{ '--bar-width': `${barW}%` }}>
              <span className={`advtable-stats-val ${t.value == null ? 'null' : ''}`}>
                {t.value == null ? '(null)' : String(t.value)}
              </span>
              <span className="advtable-stats-pct">{formatPct(pct)}</span>
            </div>
          );
        })}
        {!expanded && remaining > 0 && (
          <span className="advtable-stats-more" onClick={onToggleExpanded}>
            +{formatNumber(remaining)} more
          </span>
        )}
        {expanded && (
          <span className="advtable-stats-more" onClick={onToggleExpanded}>
            show less
          </span>
        )}
      </div>
    );
  }

  if (type === TYPE_BOOLEAN) {
    const total = (stats.true_count || 0) + (stats.false_count || 0);
    if (!total) return null;
    const trueBar = (stats.true_count / total) * 100;
    const falseBar = (stats.false_count / total) * 100;
    return (
      <div className="advtable-stats">
        <div className="advtable-stats-row" style={{ '--bar-width': `${trueBar}%` }}>
          <span className="advtable-stats-val">true</span>
          <span className="advtable-stats-pct">{formatPct(stats.true_count / total)}</span>
        </div>
        <div className="advtable-stats-row" style={{ '--bar-width': `${falseBar}%` }}>
          <span className="advtable-stats-val">false</span>
          <span className="advtable-stats-pct">{formatPct(stats.false_count / total)}</span>
        </div>
      </div>
    );
  }

  if (type === TYPE_NUMERIC) {
    return (
      <div className="advtable-stats">
        <div className="advtable-num-line"><span>min</span><span>{formatNumber(stats.min)}</span></div>
        <div className="advtable-num-line"><span>max</span><span>{formatNumber(stats.max)}</span></div>
        <div className="advtable-num-line"><span>avg</span><span>{formatNumber(stats.avg)}</span></div>
        {stats.distinct_count != null && (
          <span className="advtable-stats-more">
            {formatNumber(stats.distinct_count)} distinct
          </span>
        )}
      </div>
    );
  }

  if (type === TYPE_TEMPORAL) {
    const fmt = (v) => v ? String(v).slice(0, 10) : '—';
    return (
      <div className="advtable-stats">
        <div className="advtable-num-line"><span>min</span><span>{fmt(stats.min)}</span></div>
        <div className="advtable-num-line"><span>max</span><span>{fmt(stats.max)}</span></div>
        {stats.range_days != null && (
          <span className="advtable-stats-more">
            {formatNumber(stats.range_days)} days
          </span>
        )}
      </div>
    );
  }

  return null;
}

/* ────────────────────── Filter popovers ─────────────────────────── */

function CategoricalFilter({ values, current, onChange, onClose }) {
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return values;
    return values.filter(v => String(v.value ?? '').toLowerCase().includes(s));
  }, [values, search]);

  const selected = current?.values || new Set();
  const toggle = (v) => {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v); else next.add(v);
    onChange(next.size ? { type: 'set', values: next } : null);
  };
  const selectAll = () => onChange({ type: 'set', values: new Set(values.map(v => v.value)) });
  const clear = () => onChange(null);

  return (
    <div className="advtable-filter-popover">
      <Input
        size="small"
        placeholder="Search…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        autoFocus
      />
      <div className="advtable-filter-list">
        {filtered.length === 0 && (
          <div style={{ padding: 12, color: '#999', textAlign: 'center', fontSize: 12 }}>No matches</div>
        )}
        {filtered.map((v, i) => {
          const checked = selected.has(v.value);
          return (
            <div key={i} className="advtable-filter-row" onClick={() => toggle(v.value)}>
              <span style={{ display: 'flex', alignItems: 'center', flex: 1, overflow: 'hidden' }}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => {}}
                  style={{ marginRight: 6 }}
                />
                <span className="label" style={v.value == null ? { color: '#bfbfbf', fontStyle: 'italic' } : null}>
                  {v.value == null ? '(null)' : String(v.value)}
                </span>
              </span>
              <span className="count">{v.count}</span>
            </div>
          );
        })}
      </div>
      <div className="advtable-filter-actions">
        <Button size="small" type="link" onClick={clear} disabled={!current}>Clear</Button>
        <Button size="small" type="link" onClick={selectAll}>Select all</Button>
        <Button size="small" type="primary" onClick={onClose}>Done</Button>
      </div>
    </div>
  );
}

function NumericFilter({ stats, current, onChange, onClose }) {
  const [min, setMin] = useState(current?.min ?? stats?.min ?? null);
  const [max, setMax] = useState(current?.max ?? stats?.max ?? null);

  const apply = () => {
    if (min == null && max == null) onChange(null);
    else onChange({ type: 'range', min, max });
    onClose();
  };
  const clear = () => { setMin(stats?.min ?? null); setMax(stats?.max ?? null); onChange(null); };

  return (
    <div className="advtable-filter-popover" style={{ width: 220 }}>
      <Space direction="vertical" size={6} style={{ width: '100%' }}>
        <div style={{ display: 'flex', gap: 6 }}>
          <InputNumber size="small" placeholder="min" value={min} onChange={setMin} style={{ flex: 1 }} />
          <InputNumber size="small" placeholder="max" value={max} onChange={setMax} style={{ flex: 1 }} />
        </div>
        <div style={{ fontSize: 11, color: '#888' }}>
          range: {formatNumber(stats?.min)} – {formatNumber(stats?.max)}
        </div>
      </Space>
      <div className="advtable-filter-actions">
        <Button size="small" type="link" onClick={clear} disabled={!current}>Clear</Button>
        <Button size="small" type="primary" onClick={apply}>Apply</Button>
      </div>
    </div>
  );
}

function TemporalFilter({ stats, current, onChange, onClose }) {
  const { RangePicker } = DatePicker;
  const initial = current?.range || [null, null];
  const [range, setRange] = useState(initial);

  const apply = () => {
    if (!range || (!range[0] && !range[1])) onChange(null);
    else onChange({ type: 'date_range', range });
    onClose();
  };
  const clear = () => { setRange([null, null]); onChange(null); };

  return (
    <div className="advtable-filter-popover" style={{ width: 280 }}>
      <RangePicker
        size="small"
        value={range}
        onChange={(v) => setRange(v || [null, null])}
        style={{ width: '100%' }}
      />
      {(stats?.min || stats?.max) && (
        <div style={{ fontSize: 11, color: '#888', marginTop: 6 }}>
          source range: {String(stats?.min || '').slice(0, 10)} → {String(stats?.max || '').slice(0, 10)}
        </div>
      )}
      <div className="advtable-filter-actions">
        <Button size="small" type="link" onClick={clear} disabled={!current}>Clear</Button>
        <Button size="small" type="primary" onClick={apply}>Apply</Button>
      </div>
    </div>
  );
}

/* ────────────────────── Main component ──────────────────────────── */

export const AdvancedTable = ({ data, dataSummary, maxRows = 200, fill = false }) => {
  const [sortBy, setSortBy] = useState(null);     // { colIdx, direction }
  const [filters, setFilters] = useState({});     // colIdx → filter spec
  const [openFilter, setOpenFilter] = useState(null); // colIdx of currently-open dropdown
  const [expandedStats, setExpandedStats] = useState(new Set()); // colIdxs with expanded stats panel
  // Show column stats (distinct counts, top values, ranges) under each
  // header. Persisted globally; default off so tables open clean.
  const [showStats, setShowStats] = useState(() => {
    try { return localStorage.getItem('advtable.showStats') === '1'; } catch { return false; }
  });
  const toggleShowStats = () => setShowStats((prev) => {
    const next = !prev;
    try { localStorage.setItem('advtable.showStats', next ? '1' : '0'); } catch { /* ignore */ }
    return next;
  });

  const columns = data?.columns || [];
  const rows = data?.rows || [];

  /* Column types — prefer server-computed stats when available */
  const columnTypes = useMemo(() => columns.map((col, idx) => {
    const stats = dataSummary?.by_column?.[col];
    const sample = rows.slice(0, 30).map(r => r[idx]);
    return detectColumnType(stats, sample);
  }), [columns, rows, dataSummary]);

  /* Stats per column — server-computed when present, else client-side */
  const columnStats = useMemo(() => columns.map((col, idx) => {
    const fromServer = dataSummary?.by_column?.[col];
    if (fromServer) return fromServer;
    return clientSideStats(rows.map(r => r[idx]), columnTypes[idx]);
  }), [columns, rows, columnTypes, dataSummary]);

  /* Distinct values + counts per column for the filter dropdown */
  const distinctValues = useMemo(() => columns.map((_, idx) => {
    const counts = new Map();
    rows.forEach(r => {
      const v = r[idx];
      counts.set(v, (counts.get(v) || 0) + 1);
    });
    return Array.from(counts.entries())
      .map(([value, count]) => ({ value, count }))
      .sort((a, b) => b.count - a.count);
  }), [columns, rows]);

  /* Apply filters (over all rows) */
  const filteredRows = useMemo(() => {
    const entries = Object.entries(filters).filter(([, f]) => f != null);
    if (!entries.length) return rows;
    return rows.filter(row => {
      for (const [key, f] of entries) {
        const idx = Number(key);
        const cell = row[idx];
        if (f.type === 'set') {
          if (!f.values.has(cell)) return false;
        } else if (f.type === 'range') {
          if (cell == null) return false;
          const num = Number(cell);
          if (Number.isNaN(num)) return false;
          if (f.min != null && num < f.min) return false;
          if (f.max != null && num > f.max) return false;
        } else if (f.type === 'date_range') {
          if (cell == null) return false;
          const t = dayjs(cell);
          if (!t.isValid()) return false;
          if (f.range[0] && t.isBefore(f.range[0], 'day')) return false;
          if (f.range[1] && t.isAfter(f.range[1], 'day')) return false;
        }
      }
      return true;
    });
  }, [rows, filters]);

  /* Apply sort */
  const sortedRows = useMemo(() => {
    if (!sortBy) return filteredRows;
    const { colIdx, direction } = sortBy;
    const isNumeric = columnTypes[colIdx] === TYPE_NUMERIC;
    return [...filteredRows].sort((a, b) => {
      const av = a[colIdx]; const bv = b[colIdx];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;   // nulls last
      if (bv == null) return -1;
      let cmp;
      if (isNumeric) cmp = Number(av) - Number(bv);
      else cmp = String(av).localeCompare(String(bv));
      return direction === 'asc' ? cmp : -cmp;
    });
  }, [filteredRows, sortBy, columnTypes]);

  const visibleRows = sortedRows.slice(0, maxRows);

  /* Sort + filter handlers */
  const toggleSort = (idx) => {
    setSortBy(prev => {
      if (!prev || prev.colIdx !== idx) return { colIdx: idx, direction: 'asc' };
      if (prev.direction === 'asc') return { colIdx: idx, direction: 'desc' };
      return null;
    });
  };
  const setFilter = (idx, spec) => {
    setFilters(prev => {
      const next = { ...prev };
      if (spec == null) delete next[idx];
      else next[idx] = spec;
      return next;
    });
  };
  const clearAllFilters = () => { setFilters({}); setSortBy(null); };
  const toggleExpandStats = (idx) => setExpandedStats(prev => {
    const next = new Set(prev);
    if (next.has(idx)) next.delete(idx); else next.add(idx);
    return next;
  });

  if (!columns.length) {
    return <div className="advtable-empty">No data</div>;
  }

  const totalRows = data?.row_count || rows.length;
  const filteredCount = sortedRows.length;
  const filterCount = Object.keys(filters).length;
  const hasFilters = filterCount > 0 || sortBy != null;

  return (
    <div className={`advtable-wrap ${fill ? 'fill' : ''}`}>
      <div className="advtable-toolbar">
        <span>
          {filteredCount === totalRows
            ? `${formatNumber(totalRows)} rows × ${columns.length} columns`
            : `${formatNumber(filteredCount)} of ${formatNumber(totalRows)} rows · ${columns.length} columns`}
          {data?.truncated && ' · truncated at source'}
          {filterCount > 0 && <Tag color="blue" style={{ marginLeft: 8 }}>{filterCount} filter{filterCount > 1 ? 's' : ''}</Tag>}
          {sortBy && <Tag color="purple">sorted: {columns[sortBy.colIdx]} {sortBy.direction}</Tag>}
        </span>
        <Space size={4}>
          {hasFilters && (
            <Button size="small" type="link" icon={<ReloadOutlined />} onClick={clearAllFilters}>Reset</Button>
          )}
          <Tooltip title={showStats ? 'Hide column stats' : 'Display column stats'}>
            <Button
              size="small"
              type={showStats ? 'primary' : 'text'}
              icon={showStats ? <ProfileOutlined /> : <BarsOutlined />}
              onClick={toggleShowStats}
              aria-label={showStats ? 'Hide column stats' : 'Display column stats'}
            >
              {showStats ? 'Hide column stats' : 'Display column stats'}
            </Button>
          </Tooltip>
        </Space>
      </div>

      <div className="advtable-scroll">
        <table className="advtable">
          <thead>
            <tr>
              {columns.map((col, idx) => {
                const type = columnTypes[idx];
                const stats = columnStats[idx];
                const isSorted = sortBy?.colIdx === idx;
                const isFiltered = !!filters[idx];

                let dropdownContent = null;
                const close = () => setOpenFilter(null);
                if (type === TYPE_NUMERIC) {
                  dropdownContent = <NumericFilter stats={stats} current={filters[idx]} onChange={(s) => setFilter(idx, s)} onClose={close} />;
                } else if (type === TYPE_TEMPORAL) {
                  dropdownContent = <TemporalFilter stats={stats} current={filters[idx]} onChange={(s) => setFilter(idx, s)} onClose={close} />;
                } else {
                  dropdownContent = <CategoricalFilter values={distinctValues[idx]} current={filters[idx]} onChange={(s) => setFilter(idx, s)} onClose={close} />;
                }

                return (
                  <th key={idx}>
                    <div className="advtable-th-inner">
                      <div className="advtable-th-row1">
                        <Tooltip title={`${type}${stats?.distinct_count != null ? ` · ${formatNumber(stats.distinct_count)} distinct` : ''}${stats?.null_count ? ` · ${formatNumber(stats.null_count)} null` : ''}`}>
                          <span className="advtable-th-icon"><TypeIcon type={type} /></span>
                        </Tooltip>
                        <Tooltip title={col}>
                          <span className="advtable-th-name" onClick={() => toggleSort(idx)}>{col}</span>
                        </Tooltip>
                        <span className="advtable-th-actions">
                          {/* Always-visible stacked sort arrows. Active direction colored by theme. */}
                          <Tooltip title={isSorted ? `Sorted ${sortBy.direction} — click to ${sortBy.direction === 'asc' ? 'sort desc' : 'clear sort'}` : 'Sort ascending'}>
                            <button
                              type="button"
                              className="advtable-sort-btn"
                              onClick={() => toggleSort(idx)}
                              aria-label={`Sort ${col}`}
                            >
                              <CaretUpOutlined className={isSorted && sortBy.direction === 'asc' ? 'active' : ''} />
                              <CaretDownOutlined className={isSorted && sortBy.direction === 'desc' ? 'active' : ''} />
                            </button>
                          </Tooltip>
                          <Dropdown
                            trigger={['click']}
                            placement="bottomRight"
                            open={openFilter === idx}
                            onOpenChange={(o) => setOpenFilter(o ? idx : null)}
                            dropdownRender={() => (
                              <div style={{ background: '#fff', boxShadow: '0 4px 14px rgba(0,0,0,0.12)', border: '1px solid #e5e7eb', borderRadius: 6 }}>
                                {dropdownContent}
                              </div>
                            )}
                          >
                            <button
                              type="button"
                              className={`advtable-icon-btn ${isFiltered ? 'active' : ''}`}
                              title="Filter"
                            >
                              {isFiltered ? <FilterFilled /> : <FilterOutlined />}
                            </button>
                          </Dropdown>
                        </span>
                      </div>
                      {showStats && (
                        <HeaderStats
                          stats={stats}
                          type={type}
                          totalRows={totalRows}
                          distinctValues={distinctValues[idx]}
                          expanded={expandedStats.has(idx)}
                          onToggleExpanded={() => toggleExpandStats(idx)}
                        />
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {visibleRows.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="advtable-empty">
                  {hasFilters ? 'No rows match the current filters.' : 'No rows.'}
                </td>
              </tr>
            )}
            {visibleRows.map((row, ri) => (
              <tr key={ri}>
                {row.map((cell, ci) => {
                  const isNum = columnTypes[ci] === TYPE_NUMERIC;
                  return (
                    <td key={ci} className={cell == null ? 'null-cell' : (isNum ? 'numeric-cell' : '')}>
                      {cell == null ? 'null' : String(cell)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {sortedRows.length > visibleRows.length && (
        <div style={{ padding: '4px 10px', fontSize: 11, color: '#8c8c8c', borderTop: '1px solid #f0f0f0', textAlign: 'center' }}>
          Showing first {formatNumber(visibleRows.length)} of {formatNumber(sortedRows.length)} rows
        </div>
      )}
    </div>
  );
};

export default AdvancedTable;
